import React, { useState, useEffect } from 'react';
import ShipmentTracking from '../components/ShipmentTracking';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || 'en').startsWith('es') ? 'es-CO' : 'en-US';
  const statusLabel = (s) => t(`orderTracking.status.${s}`, { defaultValue: s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ') });
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (orderId && token) {
      loadOrderTracking();
    }
  }, [orderId, token]);

  const loadOrderTracking = async () => {
    try {
      const response = await axios.get(
        `${API}/tracking/orders/${orderId}/public?token=${token}`
      );
      
      setOrder(response.data.order);
      setStatusHistory(response.data.statusHistory || []);
      setTickets(response.data.tickets || []);
    } catch (error) {
      setError(error.response?.data?.detail || t('orderTracking.loadFailed'));
      toast({
        title: t('common.error'),
        description: t('orderTracking.toastDescription'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      case 'confirmed':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'processing':
        return <Package className="h-6 w-6 text-blue-500" />;
      case 'dispatched':
      case 'shipped':
      case 'in_transit':
        return <Truck className="h-6 w-6 text-purple-500" />;
      case 'delivered':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      default:
        return <Clock className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'dispatched':
      case 'shipped':
      case 'in_transit': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-900';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-[#be1f1a]"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orderTracking.notFoundTitle')}</h1>
          <p className="text-gray-600 mb-6">
            {error || t('orderTracking.notFoundDefault')}
          </p>
          <Link to="/">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {t('orderTracking.goHome')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentStatus = order.status || 'pending';

  return (
    <>
      <SEOHead
        title={t('orderTracking.seoTitle', { n: order.order_number })}
        description={t('orderTracking.seoDescription', { n: order.order_number })}
        url={`/track/${orderId}`}
        type="website"
      />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-[#2C2420] mb-2">
                  {t('checkoutSuccess.orderNumberLabel', { n: order.order_number })}
                </h1>
                <p className="text-gray-600">
                  {t('orderTracking.placedOn', { date: new Date(order.created_at).toLocaleDateString(dateLocale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) })}
                </p>
              </div>
              <div className={`px-4 py-2 rounded-full font-medium ${getStatusColor(currentStatus)}`}>
                {statusLabel(currentStatus)}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-6 pt-6 border-t">
              <div>
                <p className="text-sm text-gray-600">{t('orderTracking.totalAmount')}</p>
                <p className="text-lg font-semibold text-[#2C2420]">
                  {order.currency} {order.total?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('orderTracking.customer')}</p>
                <p className="text-lg font-semibold text-[#2C2420]">
                  {order.shipping_address?.name || order.email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('orderTracking.deliveryAddress')}</p>
                <p className="text-sm text-[#2C2420]">
                  {order.shipping_address?.address}<br/>
                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}
                </p>
              </div>
            </div>
          </div>

          <ShipmentTracking shipment={order.shipment} className="mb-6" />

          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-[#2C2420] mb-6">{t('orderTracking.timelineTitle')}</h2>
            
            {statusHistory.length > 0 ? (
              <div className="space-y-4">
                {statusHistory.map((entry, index) => (
                  <div key={entry.id || index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="p-2 rounded-full bg-gray-100">
                        {getStatusIcon(entry.status)}
                      </div>
                      {index < statusHistory.length - 1 && (
                        <div className="w-0.5 h-12 bg-gray-200 my-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-[#2C2420] capitalize">
                          {statusLabel(entry.status)}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {entry.notes && (
                        <p className="text-gray-600 text-sm">{entry.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                {t('orderTracking.noUpdates')}
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-[#2C2420] mb-4">{t('orderTracking.orderItems')}</h2>
            <div className="space-y-4">
              {order.items?.map((item, index) => (
                <div key={index} className="flex gap-4 pb-4 border-b last:border-0">
                  <img
                    src={item.image || '/placeholder.jpg'}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#2C2420]">{item.name}</h3>
                    <p className="text-sm text-gray-600">{t('orderTracking.quantity', { n: item.quantity })}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#2C2420]">
                      {order.currency} {item.price?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#be1f1a]/10 to-[#a71b17]/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <MessageSquare className="h-6 w-6 text-[#be1f1a] mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#2C2420] mb-2">{t('orderTracking.needHelp')}</h2>
                <p className="text-gray-700 mb-4">
                  {t('orderTracking.needHelpDesc')}
                </p>
                {tickets.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-semibold text-[#2C2420]">
                      {t('orderTracking.yourTickets')}
                    </p>
                    {tickets.map(ticket => (
                      <Link
                        key={ticket.id}
                        to={`/support/ticket/${ticket.id}`}
                        className="block p-3 bg-white rounded border hover:border-[#be1f1a] transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{ticket.subject}</span>
                          <span className={`text-xs px-2 py-1 rounded ${ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {t(`orderTracking.ticketStatus.${ticket.status}`, { defaultValue: ticket.status })}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : null}
                <Link to={`/support/create?orderId=${order.order_number}&token=${token}`}>
                  <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {t('supportTickets.createTicket')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderTrackingPage;